import React, { useRef } from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Image,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  Paragraph, Taxonomy, View,
} from '@uncinc/react-drupal-core';

import {
  Share,
  GetStarted,
  WysiwygField,
} from '../../../../components';

import './style.scss';
import NodeMetadata from '../../../../components/NodeMetadata';
import {
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
} from '../../../../config/constants';
import ExpertsSlider from '../../../../components/ExpertsSlider';
import NewsletterBlock from '../../../../components/NewsletterBlock';
import MemberWall from '../../../../components/MemberWall';
import useLoggedInStatus from '../../../../hooks/useLoggedInstatus';
import Tags from '../../../../components/Tags';

const NodeResearchDetail = ({ entity, className }) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useLoggedInStatus();
  const classes = classNames(className, 'NodeResearchDetail');
  const theme = entity.getIn(['field_theme', 0, 'entity']);

  const publicationDate = entity.getIn(['field_publication_date', 0, 'value']) ? new Date(Date.parse(entity.getIn(['field_publication_date', 0, 'value']))) : null;
  const expectedPublicationDate = entity.getIn(['field_expected_publication_date', 0, 'value']) ? new Date(Date.parse(entity.getIn(['field_expected_publication_date', 0, 'value']))) : null;
  const title = entity.getIn(['title', 0, 'value'], '');
  const subtitle = entity.getIn(['field_subtitle', 0, 'value']);
  const body = entity.getIn(['body', 0, 'processed']);
  const type = entity.getIn(['field_research_type', 0, 'entity', 'name', 0, 'value'], 'Artikel');
  const experts = entity.getIn(['field_experts'], Immutable.Map());
  const allParagraphs = entity.getIn(['field_paragraphs'], Immutable.Map());
  const getStartedContent = entity.getIn(['field_get_started_content'], Immutable.Map()).concat(entity.getIn(['field_get_started_tax_content']));
  const isMemberOnlyContent = entity.getIn(['field_member_only_content', 0, 'value'], false);

  const showMemberWall = !isLoggedIn && isMemberOnlyContent;

  const paragraphs = allParagraphs.filter((item) => {
    const itemType = item.getIn(['entity', 'type', 0, 'target_id']);
    return itemType !== 'cta';
  });

  const ctaParagraphs = allParagraphs.filter((item) => {
    const itemType = item.getIn(['entity', 'type', 0, 'target_id']);
    return itemType === 'cta';
  });

  const getStartedRef = useRef();

  const subjects = entity.getIn(['field_subject']);
  const workfields = entity.getIn(['field_workfield']);
  const relatedDossierArgs = entity.getIn(['field_subject'], Immutable.List())
    .concat(entity.getIn(['field_workfield'], Immutable.List()));
  const expertsTitle = entity.getIn(['field_experts_title', 0, 'value'], t('expert.node_title'));

  return (
    <article typeof="schema:WebPage" className={classes}>
      <Container>
        {entity.getIn(['field_media_image', 0]) && (
          <div className="NodeResearchDetail__hero">
            <Image
              lazyload={false}
              className="NodeResearchDetail__hero__image"
              includeWebP
              src={{
                [SCREEN_MOBILE]: entity.getIn(['field_media_image', 0, 'styles', 'hero_mobile']),
                [SCREEN_TABLET]: entity.getIn(['field_media_image', 0, 'styles', 'hero_tablet']),
                [SCREEN_NORMAL]: entity.getIn(['field_media_image', 0, 'styles', 'hero_desktop']),
              }}
              alt={entity.getIn(['field_media_image', 0, 'alt'], '')}
            />
          </div>
        )}
        <div className="NodeResearchDetail__content__wrapper row">
          <div className="col-12 col-lg-10 offset-lg-1">
            <div className="NodeResearchDetail__content" id="main">
              <NodeMetadata
                icon="icon-copy"
                displayType={type}
                theme={theme}
              />
              <h1>{title}</h1>

              {subtitle && (
                <div className="NodeResearchDetail__subtitle">
                  {subtitle}
                </div>
              )}

              {body && (
                <WysiwygField
                  property="schema:text"
                  className="NodeResearchDetail__body subheading2"
                  html={body}
                />
              )}

              <div className="NodeIntervention__content__steps">
                <div className="container">
                  <div className="row">
                    <div className="col-12">
                      <h2 className="asH4">Status</h2>
                      <p>{entity.getIn(['field_research_status', 0, 'entity', 'name', 0, 'value'])}</p>
                    </div>
                    <div className="col-12">
                      <h2 className="asH4">Onderzoek type</h2>
                      <p>{entity.getIn(['field_research_type', 0, 'entity', 'name', 0, 'value'])}</p>
                    </div>
                  </div>
                </div>
              </div>


              {expectedPublicationDate && !publicationDate && (
                <div className="NodeResearchDetail__info">
                  <div className="NodeResearchDetail__changed">
                    Verwachte publicatiedatum: {`${expectedPublicationDate.getDate()}-${expectedPublicationDate.getMonth() + 1}-${expectedPublicationDate.getFullYear()}`}
                  </div>
                </div>
              )}


              {publicationDate && (
                <div className="NodeResearchDetail__info">
                  <div className="NodeResearchDetail__changed">
                    Publicatiedatum: {`${publicationDate.getDate()}-${publicationDate.getMonth() + 1}-${publicationDate.getFullYear()}`}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>

      <div className="NodeContentWrapper">
        {paragraphs.size > 0 && (
          <div className="Paragraphs">
            {
              paragraphs.map((item, index) => {
                if (showMemberWall && index > 0) return;
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                );
              })
            }
          </div>
        )}

        {ctaParagraphs.size > 0 && (
          <div className="CTA__Paragraphs">
            {
              ctaParagraphs.map((item, index) => {
                if (showMemberWall && index > 0) return;
                const paragraphId = item.get('target_id');
                const paragraph = item.getIn(['entity']);

                return (
                  <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} />
                );
              })
            }
          </div>
        )}

        <ExpertsSlider
          expertsTitle={expertsTitle}
          experts={experts}
        />

        <GetStarted
          title={entity.getIn(['field_get_started_title', 0, 'value'])}
          subtitle={entity.getIn(['field_get_started_subtitle', 0, 'value'])}
          content={getStartedContent}
          getStartedRef={getStartedRef}
        />

        {relatedDossierArgs.size > 0 && (
          <div className="TaxonomySubject__related_dossiers">
            <Container>
              <h2>{entity.getIn(['field_related_dossiers_title', 0, 'value'], 'Dit artikel is onderdeel van')}</h2>
              <View id="article_dossiers_related" display="block_1" args={relatedDossierArgs} />
            </Container>
          </div>
        )}

        {entity.get('field_paragraph_feedback') ? (
          <div className="TaxonomySubject__feedback">
            <Container>
              {entity.get('field_paragraph_feedback', Immutable.List()).map((paragraph) => {
                const id = paragraph.get('target_id');
                const language = paragraph.getIn(['entity', 'langcode', 0, 'value']);
                return (
                  <Paragraph
                    key={`paragraph:${id}:${language}`}
                    id={id}
                    entity={paragraph.get('entity')}
                  />
                );
              })}
            </Container>
          </div>
        ) : null}

        <Tags subjects={subjects} workfields={workfields} />
        {theme && (
          <Container>
            <div className="row">
              <div className="col-12">
                <Taxonomy
                  id={theme.getIn(['tid', 0, 'value'])}
                  entity={theme}
                  viewMode="card"
                />
              </div>
            </div>
          </Container>
        )}
        <Container>
          <div className="row">
            <div className="col-12 col-lg-8 offset-lg-2">
              <Share pageTitle={entity.getIn(['title', 0, 'value'], '')} />
            </div>
          </div>
        </Container>
      </div>

      {/* Member wall. */}
      {showMemberWall && (
        <MemberWall />
      )}

      {/* Newsletter subscription block */}
      <NewsletterBlock />
    </article>
  );
};

export default NodeResearchDetail;
